/**
 * Prints a block of already-rendered read-only text — the legal documents behind the
 * registration form's AGB checkbox — by writing it into a blank window that prints itself.
 *
 * Ported from C:\dev\gen\src\utils\printTextDocument.ts. The only changes are mechanical:
 * gen targets a newer lib and uses String.replaceAll, which does not exist under this
 * project's `lib: ["DOM", "ES2020"]`, so the escapers use global regexes instead.
 *
 * Printing the *innerHTML* of the scroll container is what keeps the overlay's own chrome —
 * overflow-y-auto, min-h-0, flex-1, max-h-full, rounded corners, shadow, backdrop — out of
 * the printed document entirely.
 */

export interface TextPrintDocumentOptions {
	/** Document title, already localized. Fills <title> and the page-1 heading. */
	heading: string;

	/** innerHTML of the element to print. */
	bodyHtml: string;

	/** Stylesheets cloned from the app document, so Tailwind classes resolve. */
	styles: string;
	baseUrl: string;
}

function escapeAttribute(value: string): string {
	return value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeText(value: string): string {
	return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

export function buildTextPrintDocumentHtml({ heading, bodyHtml, styles, baseUrl }: TextPrintDocumentOptions): string {
	const safeHeading = escapeText(heading);

	return `<!doctype html>
<html lang="de">
<head>
<meta charset="utf-8">
<title>${safeHeading}</title>
<base href="${escapeAttribute(baseUrl)}">
${styles}
<style>
@page { size: A4 portrait; margin: 20mm 18mm; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
body { font-size: 10pt; line-height: 1.5; color: #000; overflow-wrap: break-word; }
[data-print-heading="true"] { font-size: 15pt; font-weight: 600; margin: 0 0 8mm; }
[data-print-body="true"] * { max-height: none !important; overflow: visible !important; }
[data-print-body="true"] h2 { font-size: 13pt; font-weight: 600; break-after: avoid; page-break-after: avoid; break-inside: avoid; margin: 8mm 0 3mm; }
[data-print-body="true"] h3 { break-after: avoid; page-break-after: avoid; break-inside: avoid; margin: 6mm 0 2mm; }
[data-print-body="true"] p { orphans: 3; widows: 3; margin: 0 0 3mm; }
/* Tables must stay breakable so a long one can span pages and repeat its header;
   only the individual rows are kept whole. 8.5pt because a five-column table in
   the 174mm text width would otherwise wrap every cell to single words. */
[data-print-body="true"] table { width: 100%; margin: 0 0 4mm; border-collapse: collapse; font-size: 8.5pt; line-height: 1.35; break-inside: auto; page-break-inside: auto; }
[data-print-body="true"] thead { display: table-header-group; }
[data-print-body="true"] tr { break-inside: avoid; page-break-inside: avoid; }
[data-print-body="true"] th, [data-print-body="true"] td { border: 0.2mm solid #c9c7d1; padding: 1.2mm 1.5mm; text-align: left; vertical-align: top; }
[data-print-body="true"] th { background: #f7f1ed; font-weight: 600; }
</style>
</head>
<body>
<h1 data-print-heading="true">${safeHeading}</h1>
<div data-print-body="true" lang="de">${bodyHtml}</div>
<script>
window.addEventListener("load", async function () {
  if (document.fonts && document.fonts.ready) await document.fonts.ready;
  window.print();
});
</script>
</body>
</html>`;
}

/** Clones the app's stylesheets so the printed markup keeps its Tailwind classes. */
function collectDocumentStyles(): string {
	return Array.from(document.head.querySelectorAll('link[rel="stylesheet"], style'))
		.map((node) => node.outerHTML)
		.join("\n");
}

/**
 * Opens a print window for `content` and hands it to the browser's print dialog, where the
 * user can pick a printer or "Save as PDF". Call it synchronously from a click handler, or
 * popup blockers will swallow the window.
 */
export function printTextDocument(content: HTMLElement, heading: string): void {
	const printWindow = window.open("", "_blank");

	if (!printWindow) {
		return;
	}

	printWindow.document.open();
	printWindow.document.write(
		buildTextPrintDocumentHtml({
			heading,
			bodyHtml: content.innerHTML,
			styles: collectDocumentStyles(),
			baseUrl: window.location.href
		})
	);
	printWindow.document.close();
}
